<template>
  <div class="medication-pagination">
    <!-- Left: Empty -->
    <div class="pagination-left"></div>

    <!-- Middle: Page buttons -->
    <div class="pagination-center">
      <button
        class="page-arrow-btn"
        :disabled="currentPage === 1"
        @click="goToPage(currentPage - 1)"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M10 12L6 8L10 4" stroke="#374957" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <div class="page-numbers">
        <button
          v-for="page in visiblePages"
          :key="page"
          class="page-num-btn"
          :class="{ active: page === currentPage, ellipsis: page === '...' }"
          @click="goToPage(page)"
        >
          {{ page }}
        </button>
      </div>

      <button
        class="page-arrow-btn"
        :disabled="currentPage === totalPages"
        @click="goToPage(currentPage + 1)"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M6 12L10 8L6 4" stroke="#374957" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>

    <!-- Right: Per page selector using SingleSelector -->
    <div class="pagination-right">
      <SingleSelector
        :modelValue="pageSize.toString()"
        :options="pageSizeOptionsFormatted"
        placeholder="5 per page"
        :hasLabel="false"
        @change="handlePageSizeChange"
      />
    </div>
  </div>
</template>

<script>
import SingleSelector from './SingleSelector.vue';

export default {
  name: 'MedicationPagination',
  components: {
    SingleSelector,
  },
  props: {
    total: {
      type: Number,
      default: 0
    },
    pageSize: {
      type: Number,
      default: 5
    },
    currentPage: {
      type: Number,
      default: 1
    },
    pageSizeOptions: {
      type: Array,
      default: () => [5, 10, 15]
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize) || 1;
    },
    pageSizeOptionsFormatted() {
      return this.pageSizeOptions.map(size => ({
        value: size.toString(),
        label: `${size} per page`
      }));
    },
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;

      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        // Always show first page
        pages.push(1);

        if (current <= 4) {
          for (let i = 2; i <= 5; i++) {
            pages.push(i);
          }
          if (total > 5) pages.push('...');
        } else if (current >= total - 3) {
          if (total > 5) pages.push('...');
          for (let i = total - 3; i <= total; i++) {
            pages.push(i);
          }
        } else {
          pages.push('...');
          for (let i = current - 1; i <= current + 1; i++) {
            pages.push(i);
          }
          pages.push('...');
        }

        if (!pages.includes(total) && total > 1) {
          pages.push(total);
        }
      }

      return pages;
    }
  },
  methods: {
    goToPage(page) {
      if (page === '...' || page === this.currentPage) return;
      if (page >= 1 && page <= this.totalPages) {
        this.$emit('page-change', page);
      }
    },
    handlePageSizeChange(value) {
      const newSize = parseInt(value);
      this.$emit('page-size-change', newSize);
    }
  }
};
</script>

<style scoped>
.medication-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px;
  gap: 16px;
}

.pagination-left {
  width: 25%;
}

.pagination-center {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 50%;
}

.pagination-right {
  width: 25%;
  display: flex;
  justify-content: flex-end;
}

.page-arrow-btn {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 21px;
  background: var(--Blue-10, #E4F3F5);
  cursor: pointer;
  transition: all 0.2s;
  padding: 0;
}

.page-arrow-btn:hover:not(:disabled) {
  background: var(--Blue-20, #D0EAF5);
}

.page-arrow-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  align-items: center;
  gap: 4px;
}

.page-num-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 14px;
  background: transparent;
  cursor: pointer;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #333333;
  letter-spacing: 0.4px;
  transition: all 0.2s;
}

.page-num-btn:hover:not(.active) {
  background: rgba(55, 73, 87, 0.1);
  color: #004879;
}

.page-num-btn.active {
  background: var(--VibrantDarkBlue, #004879);
  color: #ffffff;
}

/* Ellipsis styling - only disable click on ellipsis */
.page-num-btn.ellipsis {
  pointer-events: none;
}

/* SingleSelector width override */
.pagination-right :deep(.el-select) {
  width: 140px;
}

.pagination-right :deep(.el-select .el-input__wrapper) {
  border-radius: 4px;
  border: 1px solid #c4c4c4;
  height: 32px;
  box-shadow: none;
  background: #ffffff;
}

.pagination-right :deep(.el-select .el-input__wrapper:hover) {
  border-color: #004879;
}

.pagination-right :deep(.el-select .el-input__wrapper.is-focus) {
  border-color: #004879;
  box-shadow: none;
}

.pagination-right :deep(.el-select .el-input__inner) {
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-size: 14px;
  color: #333333;
}
</style>
